import React from 'react'
import { Tabs, Tab, Box, Chip, Typography, Tooltip, IconButton } from '@mui/material'
import {
  Source as SourceIcon,
  Star as StarIcon,
  Archive as ArchiveIcon
} from '@mui/icons-material'
import { Proposal } from '@/types'

interface SourceTabsProps {
  /** Liste des propositions sources triées par priorité */
  sourceProposals: Proposal[]
  /** Index de la source active */
  activeIndex: number
  /** Callback quand on change de source */
  onChangeSource: (index: number) => void
  /** Callback d'archivage d'une source (rendu uniquement sur les sources non read-only) */
  onArchiveSource?: (proposalId: string) => void
  /** Désactive les icônes archiver pendant une opération en cours */
  isArchiving?: boolean
}

/**
 * Retourne le label court pour un type d'agent
 */
function getAgentLabel(agentName: string | undefined): string {
  if (!agentName) return 'Agent'
  
  const name = agentName.toLowerCase()
  if (name.includes('ffa')) return 'FFA'
  if (name.includes('slack')) return 'Slack'
  if (name.includes('google')) return 'Google'
  
  // Prendre les 10 premiers caractères si le nom est long
  return agentName.length > 10 ? agentName.substring(0, 10) + '...' : agentName
}

/**
 * Retourne la couleur du chip selon le type d'agent
 */
function getAgentColor(agentName: string | undefined): 'primary' | 'secondary' | 'default' | 'success' | 'warning' {
  if (!agentName) return 'default'

  const name = agentName.toLowerCase()
  if (name.includes('ffa')) return 'primary'
  if (name.includes('slack')) return 'secondary'
  if (name.includes('google')) return 'warning'

  return 'default'
}

/**
 * Indique si une proposition source est en lecture seule (non modifiable côté working)
 * APPROVED / REJECTED / ARCHIVED → read-only
 * PENDING / PARTIALLY_APPROVED → éditable
 */
export function isSourceProposalReadOnly(status: string | undefined): boolean {
  return status === 'APPROVED' || status === 'REJECTED' || status === 'ARCHIVED'
}

/**
 * Retourne le badge de statut à afficher sur l'onglet d'une source non-PENDING
 */
function getStatusBadge(
  status: string | undefined
): { label: string; color: 'success' | 'error' | 'default' } | null {
  switch (status) {
    case 'APPROVED':
      return { label: 'Validée', color: 'success' }
    case 'REJECTED':
      return { label: 'Rejetée', color: 'error' }
    case 'ARCHIVED':
      return { label: 'Archivée', color: 'default' }
    default:
      return null
  }
}

/**
 * SourceTabs - Onglets pour sélectionner la source à afficher
 * 
 * Affiche un onglet par proposition source, avec :
 * - Le nom de l'agent
 * - Une étoile pour la source prioritaire (première)
 * - Le score de confiance
 */
const SourceTabs: React.FC<SourceTabsProps> = ({
  sourceProposals,
  activeIndex,
  onChangeSource,
  onArchiveSource,
  isArchiving
}) => {
  if (sourceProposals.length === 0) {
    return null
  }

  const renderArchiveButton = (proposal: Proposal) => {
    if (isSourceProposalReadOnly(proposal.status) || !onArchiveSource) return null
    return (
      <IconButton
        component="span"            // évite un <button> imbriqué dans le <Tab>
        size="small"
        color="warning"
        disabled={isArchiving}
        aria-label="Archiver cette proposition"
        title="Archiver cette proposition"
        onClick={(e) => {
          e.stopPropagation()       // n'active pas l'onglet
          onArchiveSource(proposal.id)
        }}
        sx={{ ml: 0.25, p: 0.25 }}
      >
        <ArchiveIcon sx={{ fontSize: '0.95rem' }} />
      </IconButton>
    )
  }

  // Si une seule source, afficher un simple header
  if (sourceProposals.length === 1) {
    const proposal = sourceProposals[0]
    const statusBadge = getStatusBadge(proposal.status)
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        <SourceIcon color="action" fontSize="small" />
        <Typography variant="subtitle2">
          Source unique
        </Typography>
        <Chip
          label={getAgentLabel(proposal.agent?.name)}
          size="small"
          color={getAgentColor(proposal.agent?.name)}
          variant="outlined"
        />
        {proposal.confidence && (
          <Chip
            label={`${Math.round(proposal.confidence * 100)}%`}
            size="small"
            variant="outlined"
          />
        )}
        {statusBadge && (
          <Chip
            label={statusBadge.label}
            size="small"
            color={statusBadge.color}
            variant="filled"
          />
        )}
        {renderArchiveButton(proposal)}
      </Box>
    )
  }

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Tabs
        value={activeIndex}
        onChange={(_, newValue) => onChangeSource(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 48,
          '& .MuiTab-root': {
            minHeight: 48,
            textTransform: 'none'
          }
        }}
      >
        {sourceProposals.map((proposal, index) => {
          const isPriority = index === 0
          const agentLabel = getAgentLabel(proposal.agent?.name)
          const confidence = proposal.confidence
            ? `${Math.round(proposal.confidence * 100)}%`
            : null
          const statusBadge = getStatusBadge(proposal.status)
          const readOnly = isSourceProposalReadOnly(proposal.status)

          return (
            <Tab
              key={proposal.id}
              sx={{ opacity: readOnly ? 0.65 : 1 }}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {isPriority && (
                    <Tooltip title="Source prioritaire (données les plus fiables)">
                      <StarIcon
                        fontSize="small"
                        sx={{ color: 'warning.main', mr: 0.5 }}
                      />
                    </Tooltip>
                  )}
                  <Chip
                    label={agentLabel}
                    size="small"
                    color={getAgentColor(proposal.agent?.name)}
                    variant={activeIndex === index ? 'filled' : 'outlined'}
                  />
                  {confidence && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 0.5 }}
                    >
                      {confidence}
                    </Typography>
                  )}
                  {statusBadge && (
                    <Tooltip title={`Proposition ${statusBadge.label.toLowerCase()} — lecture seule`}>
                      <Chip
                        label={statusBadge.label}
                        size="small"
                        color={statusBadge.color}
                        variant="filled"
                        sx={{ ml: 0.5, height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
                      />
                    </Tooltip>
                  )}
                  {renderArchiveButton(proposal)}
                </Box>
              }
            />
          )
        })}
      </Tabs>
    </Box>
  )
}

export default SourceTabs
